import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormSpy } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import {
  Grid,
  Typography,
  Paper,
  MuiThemeProvider,
  createStyles,
  makeStyles,
  createTheme
} from '@material-ui/core';
import { renderErrorOrLoading } from 'components/Crud';
import useCrudStyles from 'components/Crud/styles';
import { AppDispatch, RootState } from 'store';
import { commonErrorHandling } from 'store/utils';
import { mergeErrors } from 'utils/utils';
import Snackbars from 'components/Alerts/Snackbars';
import {
  Form,
  FormValues,
  yupSchemaToValidationFunc,
  Field
} from 'components/Forms';
import { SecondaryButton, SubmitButton } from 'components/Forms/Buttons';
import { addAppointment } from 'store/slices/appointments';
import { fetchAllEngineers } from 'store/slices/engineers';
import defaultTheme, { modalFormInputOverrides } from 'styles/defaultTheme';
import { appointmentDomain } from 'models';
import { EngineersSelector, engineerSlice } from './store';
import {
  validationSchema,
  processFormFields,
  appointmentFormFields,
  AppointmentFormFieldsType
} from './formConfig';
import ConflictingEngineersBanner from './ConflictingEngineersBanner';
import { useAppointmentEngineerConflicts } from './useAppointmentEngineerConflicts';

const useStyles = makeStyles(
  theme =>
    createStyles({
      validationErrors: {
        width: theme.containers.small,
        marginBottom: theme.spacing(2)
      },
      pageTitle: {
        marginBottom: theme.spacing(4)
      },
      submitButton: {
        padding: theme.spacing(1, 2)
      },
      cancelButton: {
        marginLeft: theme.spacing(0.5)
      }
    }),
  { name: 'AddAppointmentView' }
);

function AddAppointmentView() {
  const dispatch = useDispatch<AppDispatch>();
  const classes = useStyles();
  const crudClasses = useCrudStyles();
  const history = useHistory();

  const {
    appointmentConflicts,
    conflictedEngineerIds,
    selectedEngineerIds,
    onFormSpyChange,
    handleConflictResult
  } = useAppointmentEngineerConflicts();

  const engineerSelector = useSelector<RootState, EngineersSelector>(
    engineerSlice.selector
  );

  const validationFunc = yupSchemaToValidationFunc(validationSchema);

  const onSubmit = async (fieldValues: FormValues): Promise<void> => {
    try {
      const processedFields = processFormFields(fieldValues);
      const conflicts = await dispatch(addAppointment(processedFields));

      if (!conflicts?.length) {
        setTimeout(() => history.goBack(), 1000);
        return;
      }

      handleConflictResult(conflicts);
    } catch (e) {
      commonErrorHandling(e);
    }
  };

  const onFormCancel = () => {
    history.goBack();
  };

  useEffect(() => {
    dispatch(fetchAllEngineers(100));
  }, []);

  const fields = useMemo(
    () =>
      appointmentFormFields(
        AppointmentFormFieldsType.Add,
        engineerSelector.items,
        conflictedEngineerIds,
        selectedEngineerIds.length
      ),
    [engineerSelector.items, conflictedEngineerIds, selectedEngineerIds.length]
  );

  return (
    renderErrorOrLoading(
      engineerSelector.isLoading,
      'addAppointmentSpinner',
      mergeErrors([engineerSelector.error]),
      crudClasses
    ) || (
      <>
        <Grid container className={crudClasses.formContainer}>
          <Grid item xs={12}>
            <Typography
              variant="h1"
              className={classes.pageTitle}
              data-testid="createAppointmentPageTitle"
            >
              New {appointmentDomain.label}
            </Typography>
            {conflictedEngineerIds.length > 0 && (
              <ConflictingEngineersBanner
                conflictedEngineers={conflictedEngineerIds.length}
                totalEngineers={selectedEngineerIds.length}
                appointmentConflicts={appointmentConflicts}
                className={clsx(crudClasses.root, classes.validationErrors)}
              />
            )}
            <Paper className={`${crudClasses.root} ${crudClasses.paper}`}>
              <MuiThemeProvider
                theme={createTheme({
                  ...defaultTheme,
                  overrides: {
                    ...defaultTheme.overrides,
                    ...modalFormInputOverrides
                  }
                })}
              >
                <Form
                  validationFunc={validationFunc}
                  onSubmit={onSubmit}
                  goBackToPreviousPage={false}
                >
                  {() => (
                    <>
                      <FormSpy
                        subscription={{
                          values: true,
                          submitFailed: true,
                          submitting: true
                        }}
                        onChange={onFormSpyChange}
                      />
                      {fields.map(field => (
                        <Field key={field.id} field={field} />
                      ))}
                      <SubmitButton
                        testId="createAppointmentSubmit"
                        className={classes.submitButton}
                      >
                        Schedule
                      </SubmitButton>
                      <SecondaryButton
                        onClick={onFormCancel}
                        size="medium"
                        className={classes.cancelButton}
                        testId="createAppointmentCancel"
                      >
                        Cancel
                      </SecondaryButton>
                    </>
                  )}
                </Form>
              </MuiThemeProvider>
            </Paper>
          </Grid>
        </Grid>
        <Snackbars />
      </>
    )
  );
}

export default AddAppointmentView;
